import React, { useEffect, useState, useRef } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';

interface TaskHoverTooltipProps {
  title: string | React.ReactNode;
  children: React.ReactElement;
  placement?: TooltipProps['placement'];
  arrow?: boolean;
  enterDelay?: number; // Delay in ms before tooltip appears
}

const TaskHoverTooltip: React.FC<TaskHoverTooltipProps> = ({
  title,
  children,
  placement = 'right',
  arrow = true,
  enterDelay = 250, // 250ms default delay before tooltip appears
}) => {
  const [open, setOpen] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const childRef = React.useRef<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const isDarkMode =
    typeof document !== 'undefined' &&
    (
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark') ||
      document.documentElement.getAttribute('data-theme') === 'dark' ||
      document.body.getAttribute('data-theme') === 'dark'
    );
  const tooltipBg = isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)';
  const tooltipColor = isDarkMode ? '#f8fafc' : '#111111';
  const borderColor = isDarkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(15, 23, 42, 0.12)';
  const boxShadow = '0 12px 28px rgba(0, 0, 0, 0.24)';

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setOpen(false);
    const dropdown = typeof document !== 'undefined' ? document.querySelector('[data-scroll-container="true"]') : null;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('select-menu-scroll', onScroll, { passive: true });
    if (dropdown) dropdown.addEventListener('scroll', onScroll as EventListener, { passive: true } as any);

    const selectMenus = document.querySelectorAll('[class*="select__menu"]');
    selectMenus.forEach((menu) => {
      menu.addEventListener('scroll', onScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('select-menu-scroll', onScroll);
      if (dropdown) dropdown.removeEventListener('scroll', onScroll as EventListener);
      selectMenus.forEach((menu) => {
        menu.removeEventListener('scroll', onScroll);
      });
    };
  }, []);

  useEffect(() => {
    const node = anchorEl;
    if (!node) return;

    const onScroll = () => setOpen(false);
    const subscribed: HTMLElement[] = [];

    let current: HTMLElement | null = node;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const isScrollable =
        ['auto', 'scroll', 'overlay'].includes(overflowY) ||
        ['auto', 'scroll', 'overlay'].includes(overflowX);

      if (isScrollable) {
        current.addEventListener('scroll', onScroll, { passive: true });
        subscribed.push(current);
      }
      current = current.parentElement;
    }

    return () => {
      subscribed.forEach((el) => el.removeEventListener('scroll', onScroll));
    };
  }, [anchorEl]);

  const setCombinedRef = (node: HTMLElement | null) => {
    childRef.current = node;
    setAnchorEl(node);
    const { ref } = children as any;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref && typeof ref === 'object') {
      ref.current = node;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    children.props?.onMouseEnter?.(e);
    // Clear any pending hide timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    // Clear any existing show timer
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    // Start show timer with enterDelay
    if (enterDelay > 0) {
      showTimerRef.current = setTimeout(() => {
        setOpen(true);
        showTimerRef.current = null;
      }, enterDelay);
    } else {
      setOpen(true);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    children.props?.onMouseLeave?.(e);
    // Clear any pending show timer (user left before delay completed)
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    // Start hide timer - give time to move to tooltip
    hideTimerRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setOpen(false);
      }
      hideTimerRef.current = null;
    }, 150);
  };

  const child = React.cloneElement(children, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    ref: setCombinedRef,
  });

  if (!title || title === '') return children;

  // Wrap the title content to handle tooltip hover
  const wrappedTitle = (
    <div
      onMouseEnter={() => {
        setIsTooltipHovered(true);
        // Clear hide timer when hovering over tooltip
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
      }}
      onMouseLeave={() => {
        setIsTooltipHovered(false);
        // Start hide timer when leaving tooltip
        hideTimerRef.current = setTimeout(() => {
          setOpen(false);
          setIsTooltipHovered(false);
          hideTimerRef.current = null;
        }, 150);
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {title}
    </div>
  );

  return (
    <Tooltip
      title={wrappedTitle}
      placement={placement}
      arrow={arrow}
      open={open}
      disableTouchListener
      disableFocusListener
      slotProps={{
        popper: {
          style: { zIndex: 9999 },
        },
        tooltip: {
          sx: {
            bgcolor: tooltipBg,
            color: tooltipColor,
            border: `1px solid ${borderColor}`,
            boxShadow,
            padding: '0px',
            borderRadius: '10px',
            fontSize: '12px',
            margin: '6px',
            whiteSpace: 'pre-line',
            minWidth: '260px',
            maxWidth: '320px',
          },
        },
        arrow: {
          sx: {
            color: tooltipBg,
          },
        },
      }}
    >
      {child}
    </Tooltip>
  );
};

export default TaskHoverTooltip;
